/* eslint-disable react/prop-types */
import clsx from 'clsx'
import React, { useCallback, useState, useRef } from 'react'
import { Button } from 'react-bootstrap'
import { createPortal } from 'react-dom'
import { FormattedMessage } from 'react-intl'
import PopoverStickOnHover from '../../partials/content/PopoverStickOnHover'
import { DEFAULT_ACTION_HANDLERS } from './actionDefinitions'
import { useSubMenuPosition } from './useSubMenuPosition'

function PopoverMenuButton(props) {
  const {
    item,
    employee,
    context,
    onAction,
    customActionHandlers,
    showIcons = false, // Default to hiding icons
  } = props

  const {
    id,
    label,
    icon,
    action,
    onClick,
    permissionCheck,
    availabilityCheck,
    shouldRender,
    disabledPopover,
    subMenu, // Support for nested sub-menus
  } = item

  const [showSubMenu, setShowSubMenu] = useState(false)
  const menuItemRef = useRef(null)
  const subMenuRef = useRef(null)
  const closeSubMenu = useCallback(() => setShowSubMenu(false), [])
  const subMenuPosition = useSubMenuPosition(showSubMenu, menuItemRef, subMenuRef, closeSubMenu)

  if (shouldRender && !shouldRender(employee, context)) {
    return null
  }

  // Check permissions
  const hasPermission = permissionCheck ? permissionCheck(context) : true
  const availability = availabilityCheck ? availabilityCheck(employee, context) : { isAvailable: true }
  const isAvailable = availability?.isAvailable !== false

  // Note: hasExtendedCredentials check is only for special Exchange Online case (offboard-user)
  // Other actions don't check hasExtendedCredentials here - they still render but may be disabled
  const isDisabled = !hasPermission || !isAvailable

  const handleClick = () => {
    if (isDisabled) return

    // Don't trigger action if item has a sub-menu (sub-menu items handle their own actions)
    if (subMenu && subMenu.items && subMenu.items.length > 0) {
      return
    }

    // Use action ID (id property) to determine which handler to call
    const actionId = action || id

    if (onClick) {
      // Custom onClick in config takes highest precedence
      onClick(employee, context)
    } else if (actionId) {
      // Priority: customActionHandlers > onAction > defaultHandler
      if (customActionHandlers?.[actionId]) {
        customActionHandlers[actionId](employee, context)
      } else if (onAction) {
        onAction(actionId, employee, context)
      } else if (DEFAULT_ACTION_HANDLERS[actionId]) {
        // Use default handler from actionDefinitions
        DEFAULT_ACTION_HANDLERS[actionId](employee, context)
      } else {
        // Debug: log if no handler found
        console.warn(`PopoverMenu: No handler found for action "${actionId}"`, {
          availableHandlers: Object.keys(DEFAULT_ACTION_HANDLERS),
        })
      }
    }
  }

  // Get popover content for disabled state
  const getPopoverContent = () => {
    if (disabledPopover) {
      return disabledPopover
    }

    // Check for special Exchange Online case
    // INTEGRATION_TYPE.LEGACY = 0, TOKEN = 1, APP = 2
    if (
      id === 'offboard-user' &&
      context.integrationType === 'Office365' &&
      context.selectedIntegration &&
      ((context.selectedIntegration.integration_type === 0 && !context.hasExtendedCredentials) ||
        (context.selectedIntegration.integration_type === 1 && !context.hasExoCredentials) ||
        (context.selectedIntegration.integration_type === 2 && !context.hasExoCredentials))
    ) {
      return {
        title: 'ENGAGE.EXCHANGE_ONLINE_NOT_CONNECTED',
        actionButton: {
          label: 'INTEGRATION.CONNECT_EXCHANGE_ONLINE',
          onClick: context.onConnectPowershell,
        },
      }
    }

    // Default insufficient permissions popover
    if (!hasPermission) {
      return {
        title: 'ENGAGE.INSUFFICIENT_PERMISSIONS',
        actionButton: {
          label: 'INTEGRATIONS.GO_TO',
          onClick: context.navigateToIntegrations,
        },
      }
    }

    return null
  }

  const popoverContent = getPopoverContent()

  // Handle sub-menu items
  const renderSubMenuItems = () => {
    if (!subMenu || !subMenu.items || subMenu.items.length === 0) {
      return null
    }

    if (!showSubMenu) {
      return null
    }

    // Portaled next to the parent menu (inside its `.dropdown`), so the menu, which scrolls when
    // taller than the viewport, can't clip it, while it stays in the menu's stacking context (e.g.
    // above the employee details panel). Hidden until `useSubMenuPosition` has measured it; its
    // computed `top`/`left` are the only inline styles.
    const subMenuContainer = menuItemRef.current?.closest('.dropdown') ?? document.body
    return createPortal(
      <div
        ref={subMenuRef}
        className={clsx(
          'dropdown-menu dropdown-menu-fit dropdown-menu-end dropdown-menu-anim dropdown-menu-top-unround popover-submenu popover-menu-dropdown',
          { 'popover-submenu--measuring': !subMenuPosition },
        )}
        style={subMenuPosition ?? undefined}
        onMouseEnter={() => setShowSubMenu(true)}
        onMouseLeave={() => {
          // Delay closing to allow mouse to return to parent
          setTimeout(() => {
            if (!menuItemRef.current?.matches(':hover')) {
              setShowSubMenu(false)
            }
          }, 100)
        }}
      >
        <ul className="nav popover-menu-actions-nav my-1">
          {subMenu.title && (
            <li className="nav__item nav__item--title fw-normal popover-submenu__title">
              <FormattedMessage id={subMenu.title} />
            </li>
          )}
          {subMenu.items.map((subItem) => {
            const subItemConfig = typeof subItem === 'string' ? { id: subItem } : subItem
            return (
              <PopoverMenuButton
                key={subItemConfig.id}
                item={subItemConfig}
                employee={employee}
                context={context}
                onAction={onAction}
                customActionHandlers={customActionHandlers}
                showIcons={showIcons}
              />
            )
          })}
        </ul>
      </div>,
      subMenuContainer,
    )
  }

  // Render disabled item with popover
  if (isDisabled && popoverContent) {
    return (
      <PopoverStickOnHover
        component={
          <>
            <p className="p-3 pb-0" style={{ textAlign: 'left' }}>
              <FormattedMessage id={popoverContent.title} />
            </p>
            {popoverContent.actionButton && (
              <Button
                variant="link"
                onClick={() => {
                  if (popoverContent.actionButton.onClick) {
                    popoverContent.actionButton.onClick(context)
                  }
                }}
              >
                <FormattedMessage id={popoverContent.actionButton.label} />
              </Button>
            )}
          </>
        }
        placement="left-start"
        onMouseEnter={() => {}}
        delay={200}
      >
        <li className="nav__item nav__item--disabled" data-bs-toggle="collapse" style={{ padding: 0, margin: 0 }}>
          <span
            className="nav__link"
            style={{
              padding: '0.5rem 1rem',
              height: '35px',
              lineHeight: '35px',
              minHeight: '35px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {showIcons && icon && <i className={`nav__link-icon font-color-engage fa ${icon}`} />}
            <span className="nav__link nav__link-text">
              <FormattedMessage id={label} />
            </span>
            {subMenu && <i className="fa fa-chevron-right" style={{ marginLeft: 'auto', fontSize: '0.75rem' }} />}
          </span>
        </li>
      </PopoverStickOnHover>
    )
  }

  // Render item with sub-menu
  if (subMenu && subMenu.items && subMenu.items.length > 0) {
    return (
      <li
        ref={menuItemRef}
        className={clsx('nav__item', {
          'nav__item--disabled': isDisabled,
        })}
        data-bs-toggle="collapse"
        style={{ padding: 0, margin: 0 }}
        onMouseEnter={() => setShowSubMenu(true)}
        onMouseLeave={(e) => {
          // Use setTimeout to allow mouse to move to sub-menu
          const { relatedTarget } = e
          if (!relatedTarget || !relatedTarget.closest('.popover-submenu')) {
            setTimeout(() => {
              if (!document.querySelector('.popover-submenu:hover') && !menuItemRef.current?.matches(':hover')) {
                setShowSubMenu(false)
              }
            }, 100)
          }
        }}
      >
        <span
          className="nav__link"
          style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 1rem', height: '35px', minHeight: '35px' }}
        >
          {showIcons && icon && <i className={`nav__link-icon font-color-engage fa ${icon}`} />}
          <span className="nav__link nav__link-text">
            <FormattedMessage id={label} />
          </span>
          <i className="fa fa-chevron-right" style={{ marginLeft: 'auto', fontSize: '0.75rem' }} />
        </span>
        {renderSubMenuItems()}
      </li>
    )
  }

  // Render enabled or disabled (without popover) item
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <li
      onClick={handleClick}
      className={clsx('nav__item', {
        'nav__item--disabled': isDisabled,
      })}
      data-bs-toggle="collapse"
      style={{ padding: 0, margin: 0 }}
    >
      <span
        className="nav__link"
        style={{
          padding: '0.5rem 1rem',
          height: '35px',
          lineHeight: '35px',
          minHeight: '35px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {showIcons && icon && <i className={`nav__link-icon font-color-engage fa ${icon}`} />}
        <span className="nav__link nav__link-text">
          <FormattedMessage id={label} />
        </span>
      </span>
    </li>
  )
}

export default PopoverMenuButton
